import { Column, InputSelect, InputText, MdSpacing, Row } from '@alto/design-system';
// eslint-disable-next-line @alto/prefer-react-hook-form
import { Field, type FieldProps } from 'formik';
import React from 'react';
import { TypescriptUtils } from '~shared/TypescriptUtils';
import { RELATIONSHIP_CODES, RELATIONSHIP_OPTIONS } from '~shared/constants';
import { type Values } from '~shared/features/insurances/helpers/getInitialInsuranceFormValues';
import { type CreateManualInsurancePayload } from '~shared/features/insurances/types';
import { mapFormikFieldToInputProps } from '~shared/helpers/mapFieldToInputProps';
import { createValidator, validatePresentIf, validatePresentWithMessage } from '~shared/validations';

type Props = {
  readonly values: Values;
};

export const validate = createValidator<Values>({
  relationship_code: validatePresentWithMessage('Relationship'),
  first_name: validatePresentIf((_, values) => {
    return values.relationship_code !== RELATIONSHIP_CODES.SELF;
  }, 'First Name'),
  last_name: validatePresentIf((_, values) => {
    return values.relationship_code !== RELATIONSHIP_CODES.SELF;
  }, 'Last Name'),
  member_id: validatePresentWithMessage('Member ID'),
  bin: (value: string) => {
    if (!value) {
      return 'Rx BIN cannot be blank';
    }
    if (!/^\d+$/.test(value.trim())) {
      return 'Rx BIN must contain only numbers';
    }
    if (value.trim().length !== 6) {
      return 'Rx BIN must be exactly 6 digits';
    }
    if (new Set(value.split('')).size === 1) {
      return 'Rx BIN cannot have all digits be the same value';
    }
    return null;
  },
});

// redux-form fields change to empty strings when they are focused.
// do not want to send an empty string when the value should be null.
// for example, a nickname input left blank should be a null value in the database.
export const removeEmptyValues = (insurance: CreateManualInsurancePayload): CreateManualInsurancePayload => {
  TypescriptUtils.objectKeys(insurance).forEach((key) => {
    const value = insurance[key];
    if (value === '') {
      insurance[key] = undefined;
    }
  });

  return insurance;
};

export const InsuranceFormElements: React.FC<Props> = ({ values }) => {
  return (
    <Column gap="MD">
      <Field name="member_id">
        {({ field, meta }: FieldProps<Values['member_id']>) => (
          <InputText
            id="member_id"
            label="Member ID"
            placeholder="00000000"
            caption="Can also be ID Number or Enrollee ID"
            autoCapitalize="none"
            required
            {...mapFormikFieldToInputProps(field, meta)}
          />
        )}
      </Field>
      <Field name="bin">
        {({ field, meta }: FieldProps<Values['bin']>) => (
          <InputText
            id="bin"
            label="Rx BIN"
            placeholder="000000"
            caption="6 Digit Number"
            autoCapitalize="none"
            required
            {...mapFormikFieldToInputProps(field, meta)}
          />
        )}
      </Field>
      <Field name="group">
        {({ field, meta }: FieldProps<Values['group']>) => (
          <InputText
            id="group"
            label="Rx Group"
            placeholder="00000000"
            caption="Can also be RxGrp"
            autoCapitalize="none"
            {...mapFormikFieldToInputProps(field, meta)}
          />
        )}
      </Field>
      <Field name="pcn">
        {({ field, meta }: FieldProps<Values['pcn']>) => (
          <InputText
            id="pcn"
            label="Rx PCN"
            placeholder="0000000"
            autoCapitalize="none"
            {...mapFormikFieldToInputProps(field, meta)}
          />
        )}
      </Field>
      <Field name="relationship_code">
        {({ field, meta, form: { setFieldValue } }: FieldProps<Values['relationship_code']>) => (
          <InputSelect
            options={RELATIONSHIP_OPTIONS}
            label="Relationship"
            caption="Your relationship to the primary benefit holder"
            placeholder="Self, Spouse, Child, etc."
            required
            {...mapFormikFieldToInputProps(field, meta)}
            onValueChange={(value) => setFieldValue(field.name, value)}
            value={field.value}
          />
        )}
      </Field>
      {values.relationship_code !== '' && values.relationship_code !== '1' && (
        <Row>
          <Field name="first_name">
            {({ field, meta }: FieldProps<Values['first_name']>) => (
              <InputText
                label="First name"
                placeholder="Enter first name"
                autoCapitalize="none"
                required={values.relationship_code !== '1'}
                textContentType="name"
                {...mapFormikFieldToInputProps(field, meta)}
              />
            )}
          </Field>
          <MdSpacing />
          <Field name="last_name">
            {({ field, meta }: FieldProps<Values['last_name']>) => (
              <InputText
                label="Last name"
                placeholder="Enter last name"
                autoCapitalize="none"
                textContentType="familyName"
                required={values.relationship_code !== '1'}
                {...mapFormikFieldToInputProps(field, meta)}
              />
            )}
          </Field>
        </Row>
      )}
      <Field name="nickname">
        {({ field, meta }: FieldProps<Values['nickname']>) => (
          <InputText
            label="Nickname"
            placeholder="My Rx Card"
            autoCapitalize="none"
            {...mapFormikFieldToInputProps(field, meta)}
          />
        )}
      </Field>
    </Column>
  );
};
